import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Progress,
  Divider,
  Modal,
  InputNumber,
  Select,
  Form,
  Spin,
  Popover,
  Alert,
  Input,
  Upload,
  DatePicker,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  PlusOutlined,
  CopyOutlined,
  StopOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getOrderById,
  cancelOrder,
  cloneOrder,
  updateOrderStatus,
  dispatchOrder as submitOrderDispatch,
  deliverOrder as submitOrderDelivery,
  createJobs,
  createAllJobs,
} from "../../api/orderApi";
import axios from "axios";
import OrderStatusBadge from "./OrderStatusBadge";
import PriorityBadge from "./PriorityBadge";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import { DEFAULT_STAGE_NAMES } from "../job/stageMoveUtils";

const { Title, Text } = Typography;
const { confirm } = Modal;

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [poPreviewUrl, setPoPreviewUrl] = useState(null);

  // Job creation modal
  const [jobModalVisible, setJobModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [jobForm] = Form.useForm();
  const [jobLoading, setJobLoading] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [dispatchModalVisible, setDispatchModalVisible] = useState(false);
  const [dispatchSubmitting, setDispatchSubmitting] = useState(false);
  const [dispatchForm] = Form.useForm();
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
  const [deliverySubmitting, setDeliverySubmitting] = useState(false);
  const [deliveryForm] = Form.useForm();
  const [receivingFile, setReceivingFile] = useState(null);
  const [itemSearch, setItemSearch] = useState("");

  // Fetch order
  const fetchOrder = async () => {
    setLoading(true);
    try {
      const data = await getOrderById(id);
      setOrder(data);
    } catch (error) {
      toast.error("Failed to load order");
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  // Handle cancel
  const handleCancel = () => {
    confirm({
      title: "Cancel Order",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to cancel order ${order?.order_number}?`,
      okText: "Yes, Cancel It",
      okType: "danger",
      onOk: async () => {
        try {
          await cancelOrder(id, "Cancelled by user");
          toast.success("Order cancelled");
          fetchOrder();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Failed to cancel order"
          );
        }
      },
    });
  };

  const resetDispatchForm = () => {
    dispatchForm.resetFields();
    dispatchForm.setFieldsValue({
      dispatched_on: moment(),
    });
  };

  const resetDeliveryForm = () => {
    deliveryForm.resetFields();
    deliveryForm.setFieldsValue({
      delivered_on: moment(),
    });
    setReceivingFile(null);
  };

  const openDispatchModal = () => {
    resetDispatchForm();
    setDispatchModalVisible(true);
  };

  const closeDispatchModal = () => {
    setDispatchModalVisible(false);
    resetDispatchForm();
  };

  const openDeliveryModal = () => {
    resetDeliveryForm();
    setDeliveryModalVisible(true);
  };

  const closeDeliveryModal = () => {
    setDeliveryModalVisible(false);
    resetDeliveryForm();
  };

  const handleMarkDispatched = async (values) => {
    try {
      setDispatchSubmitting(true);
      const response = await submitOrderDispatch(id, {
        challan_number: values.challan_number,
        dispatched_on: values.dispatched_on
          ? values.dispatched_on.format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        notes: values.notes,
      });
      const autoDispatchCount = response.auto_dispatched_jobs?.length || 0;
      toast.success(
        autoDispatchCount > 0
          ? `Order dispatched. ${autoDispatchCount} job(s) were also marked as dispatched.`
          : "Order dispatched successfully"
      );
      closeDispatchModal();
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to record dispatch");
    } finally {
      setDispatchSubmitting(false);
    }
  };

  // Handle mark delivered
  const handleMarkDelivered = async (values) => {
    try {
      setDeliverySubmitting(true);
      await submitOrderDelivery(id, {
        invoice_number: values.invoice_number,
        delivered_on: values.delivered_on
          ? values.delivered_on.format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        notes: values.notes,
        receiving_image: receivingFile,
      });
      toast.success("Delivery details saved");
      closeDeliveryModal();
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to record delivery");
    } finally {
      setDeliverySubmitting(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === "dispatched") {
      openDispatchModal();
      return;
    }

    if (newStatus === "delivered") {
      openDeliveryModal();
      return;
    }

    try {
      await updateOrderStatus(id, newStatus);
      toast.success("Order status updated");
      fetchOrder();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Open job creation modal
  const openJobModal = (item) => {
    setSelectedItem(item);
    setJobModalVisible(true);

    const assignedQty =
      item.jobs?.reduce((sum, j) => sum + (j.ordered_quantity || j.production_quantity || j.quantity_assigned || 0), 0) || 0;
    const remainingQty = (item.quantity || 0) - assignedQty;

    jobForm.setFieldsValue({
      quantity_per_job: remainingQty > 0 ? remainingQty : item.quantity,
      job_count: 1,
    });
  };

  // Handle job creation
  const handleCreateJobs = async (values) => {
    if (!selectedItem) return;
    setJobLoading(true);
    try {
      await createJobs(order.id, selectedItem.id, values);
      toast.success("Jobs created successfully");
      setJobModalVisible(false);
      jobForm.resetFields();
      fetchOrder();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create jobs"
      );
    } finally {
      setJobLoading(false);
    }
  };

  // Item table columns
  const stageColorMap = {
    order_received: "#8c8c8c",
    pending_inventory: "#cf1322",
    inventory_fulfilled: "#1d39c4",
    ready_for_production: "#08979c",
    pre_print: "#7c3aed",
    coating: "#0f766e",
    corrugation: "#2563eb",
    sheet_cutting: "#0284c7",
    pasting: "#c026d3",
    casting: "#7c2d12",
    die_cutting: "#4f46e5",
    joining: "#9333ea",
    after_box_print: "#0369a1",
    qc: "#15803d",
    ready_for_dispatch: "#b26a00",
    dispatched: "#8a7a00",
  };

  const formatStageLabel = (stage) => {
    if (!stage) return null;
    return (
      DEFAULT_STAGE_NAMES[stage] ||
      String(stage)
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    );
  };

  const compareText = (left, right) =>
    String(left || "").localeCompare(String(right || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const filteredItems = normalizedItemSearch
    ? (order?.items || []).filter((item) => {
        const searchParts = [
          item.item_name,
          item.subcategory,
          item.length,
          item.width,
          item.height,
          item.unit_type,
          item.ply,
          item.casting_type,
          item.quantity,
          item.quantity_produced,
          item.quantity_delivered,
          item.rate_per_unit,
          item.total_amount,
          ...(item.jobs || []).flatMap((job) => [
            job.job_number,
            job.current_stage,
            formatStageLabel(job.current_stage),
            job.notes,
            job.internal_notes,
          ]),
        ];

        return searchParts
          .filter((value) => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase()
          .includes(normalizedItemSearch);
      })
    : order?.items || [];

  const itemColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      width: 280,
      sorter: (a, b) => compareText(a.item_name, b.item_name),
      render: (text, record) => (
        <Space direction="vertical" size={0} style={{ width: "100%" }}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.subcategory} | {record.length}×{record.width}×
            {record.height} {record.unit_type}
          </Text>
        </Space>
      ),
    },
    {
      title: "Specs",
      key: "specs",
      sorter: (a, b) =>
        (Number(a.ply) || 0) - (Number(b.ply) || 0) ||
        compareText(a.casting_type, b.casting_type),
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {record.ply} Ply | {record.casting_type}
          </Text>
          <Space size={4}>
            {record.is_printing && <Tag color="blue">Printing</Tag>}
            {record.is_coating && <Tag color="green">Coating</Tag>}
            {record.joining_method === "stitching" && <Tag color="purple">Stitching</Tag>}
            {record.joining_method === "hand_pasting" && <Tag color="orange">Hand Pasting</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      sorter: (a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>Ordered: {Number(record.quantity || 0).toLocaleString("en-IN")}</Text>
          <Text type="success">
            Produced: {Number(record.quantity_produced || 0).toLocaleString("en-IN")}
          </Text>
          <Text type="secondary">
            Delivered: {Number(record.quantity_delivered || 0).toLocaleString("en-IN")}
          </Text>
        </Space>
      ),
    },
    {
      title: "Rate/Unit",
      dataIndex: "rate_per_unit",
      key: "rate",
      sorter: (a, b) =>
        (Number(a.rate_per_unit) || 0) - (Number(b.rate_per_unit) || 0),
      render: (rate) => `₹${Number(rate || 0).toFixed(2)}`,
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "amount",
      sorter: (a, b) =>
        (Number(a.total_amount) || 0) - (Number(b.total_amount) || 0),
      render: (amount) =>
        `₹${Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}`,
      align: "right",
    },
    {
      title: "Jobs",
      key: "jobs",
      width: 150,
      sorter: (a, b) => (a.jobs?.length || 0) - (b.jobs?.length || 0),
      render: (_, record) => {
        const totalAssigned =
          record.jobs?.reduce(
            (sum, j) => sum + (j.ordered_quantity || j.production_quantity || j.quantity_assigned || 0),
            0
          ) || 0;
        const remainingQty = (record.quantity || 0) - totalAssigned;

        return (
          <Space direction="vertical" size={4}>
            {record.jobs && record.jobs.length > 0 ? (
              record.jobs.map((job) => (
                <Link key={job.id} to={`/job/${job.id}`}>
                  <Tag color="default" style={{ cursor: "pointer" }}>
                    {job.job_number}: {job.completed_quantity || 0}/
                    {job.production_quantity || job.quantity_assigned}
                  </Tag>
                </Link>
              ))
            ) : (
              <Text type="secondary">No jobs created</Text>
            )}
            {remainingQty > 0 &&
              !["delivered", "cancelled"].includes(order?.status) && (
                <Button
                  size="small"
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => openJobModal(record)}
                >
                  Create Jobs ({remainingQty} remaining)
                </Button>
              )}
          </Space>
        );
      },
    },
    {
      title: "Job Status",
      key: "job_statuses",
      width: 150,
      sorter: (a, b) =>
        compareText(
          a.jobs?.map((job) => formatStageLabel(job.current_stage)).join(" | "),
          b.jobs?.map((job) => formatStageLabel(job.current_stage)).join(" | ")
        ),
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          {record.jobs && record.jobs.length > 0 ? (
            record.jobs.map((job) => (
              <Tag
                key={job.id}
                color={stageColorMap[job.current_stage] || "default"}
              >
                {formatStageLabel(job.current_stage) || "-"}
              </Tag>
            ))
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Job Notes / Remarks",
      key: "job_notes",
      width: 180,
      sorter: (a, b) =>
        compareText(
          a.jobs?.map((job) => job.notes || job.internal_notes || "").join(" | "),
          b.jobs?.map((job) => job.notes || job.internal_notes || "").join(" | ")
        ),
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {record.jobs && record.jobs.length > 0 ? (
            record.jobs.map((job) => {
              const remarks = job.notes || job.internal_notes;

              return (
                <div key={job.id}>
                  {remarks ? (
                    <Text style={{ whiteSpace: "pre-wrap" }}>{remarks}</Text>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              );
            })
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="text-center" style={{ padding: 60 }}>
        <Spin size="large" tip="Loading order..." />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Title level={4}>Order not found</Title>
        <Button onClick={() => navigate("/order")}>Back to Orders</Button>
      </div>
    );
  }

  const isTerminal = ["delivered", "cancelled"].includes(order.status);
  const canClone = order.status !== "cancelled";
  const totalQty =
    order.items?.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) ||
    0;
  const producedQty =
    order.items?.reduce(
      (sum, item) => sum + (Number(item.quantity_produced) || 0),
      0
    ) || 0;
  const deliveredQty =
    order.items?.reduce(
      (sum, item) => sum + (Number(item.quantity_delivered) || 0),
      0
    ) || 0;
  const productionPct =
    totalQty > 0 ? Math.round((producedQty / totalQty) * 100) : 0;
  const deliveryPct =
    totalQty > 0 ? Math.round((deliveredQty / totalQty) * 100) : 0;
  const jobsToAutoDispatch = (order.jobs || []).filter(
    (job) => !["dispatched", "cancelled"].includes(job.status)
  );
  const autoDispatchWarning =
    jobsToAutoDispatch.length > 0
      ? `The following jobs were not marked as dispatched and will be marked as dispatched: ${jobsToAutoDispatch
          .map((job) => job.job_number)
          .join(", ")}`
      : null;

  return (
    <>
      <PageTitle title="Back" />
      <div className="mr-top">
        {/* Header Card */}
        <Card bordered={false} style={{ marginBottom: 16 }}>
          <div className="card-header d-flex justify-content-between m-3">
            <div>
              <h5>
                <i className="bi bi-box-seam"></i>
                <span className="mr-left">
                  Order: {order.order_number}
                </span>
              </h5>
              <Space>
                <Select
                  value={order.status}
                  style={{ minWidth: 160 }}
                  onChange={handleStatusUpdate}
                  disabled={isTerminal}
                >
                  <Select.Option value="confirmed">Confirmed</Select.Option>
                  <Select.Option value="planned">Planned</Select.Option>
                  <Select.Option value="in_production">In Production</Select.Option>
                  <Select.Option value="dispatched">Dispatched</Select.Option>
                  <Select.Option value="delivered">Delivered</Select.Option>
                  <Select.Option value="on_hold">On Hold</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
                <PriorityBadge priority={order.priority} />
                {order.quotation_number && (
                  <Text type="secondary">
                    from{" "}
                    <Link to={`/quotation/${order.quotation_id}`}>
                      {order.quotation_number}
                    </Link>
                  </Text>
                )}
              </Space>
            </div>
            <div className="text-end">
              <Space wrap>
                {canClone && (
                  <Button
                    shape="round"
                    icon={<CopyOutlined />}
                    onClick={() => navigate(`/order/new?cloneFrom=${id}`)}
                  >
                    Clone
                  </Button>
                )}
                {!isTerminal && (
                  <Link to={`/order/${order.id}/edit`}>
                    <Button
                      type="primary"
                      shape="round"
                      icon={<EditOutlined />}
                    >
                      Edit
                    </Button>
                  </Link>
                )}
                {order.status === "confirmed" && (!order.jobs || order.jobs.length === 0) && (
                  <Button
                    shape="round"
                    type="primary"
                    loading={jobLoading}
                    onClick={async () => {
                      try {
                        setJobLoading(true);
                        const jobs = await createAllJobs(id);
                        toast.success(`${jobs.length} job(s) created — Order is now Planned`);
                        fetchOrder();
                      } catch (err) {
                        toast.error(err.response?.data?.message || "Failed to create jobs");
                      } finally {
                        setJobLoading(false);
                      }
                    }}
                  >
                    Create Jobs
                  </Button>
                )}
                {!isTerminal && (
                  <Button
                    danger
                    shape="round"
                    icon={<StopOutlined />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                )}
              </Space>
            </div>
          </div>
        </Card>

        {/* Internal Notes Banner */}
        {order.internal_notes && (
          <Alert
            type="warning"
            showIcon
            icon={<i className="bi bi-flag-fill" style={{ color: '#fa8c16' }} />}
            message={<Text strong>Internal Notes</Text>}
            description={
              <span style={{ whiteSpace: 'pre-wrap' }}>{order.internal_notes}</span>
            }
            style={{ marginBottom: 16, border: '1px solid #ffe58f', borderLeft: '4px solid #fa8c16' }}
          />
        )}

        <Row gutter={16}>
          {/* Main content */}
          <Col xs={24} lg={poPreviewUrl ? 12 : 16}>
            {/* Order Info */}
            <Card
              bordered={false}
              title="Order Details"
              style={{ marginBottom: 16 }}
            >
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
              >
                <Descriptions.Item label="Order #">
                  {order.order_number}
                </Descriptions.Item>
                <Descriptions.Item label="Customer">
                  {order.customer?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <OrderStatusBadge status={order.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Priority">
                  <PriorityBadge priority={order.priority} />
                </Descriptions.Item>
                <Descriptions.Item label="PO Number">
                  {order.customer_po_number || "-"}
                  {order.po_document_key && (
                    <Button
                      type="link"
                      size="small"
                      icon={poPreviewUrl ? <CloseOutlined /> : <EyeOutlined />}
                      style={{ marginLeft: 8 }}
                      onClick={async () => {
                        if (poPreviewUrl) {
                          setPoPreviewUrl(null);
                          return;
                        }
                        try {
                          const { data } = await axios.get(`po-document/order/${order.id}/po-document`);
                          setPoPreviewUrl(data.url);
                        } catch {
                          toast.error("Failed to load PO document");
                        }
                      }}
                    >
                      {poPreviewUrl ? "Hide PO" : "View PO"}
                    </Button>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="PO Marking">
                  {order.po_marking_key ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={async () => {
                        try {
                          const { data } = await axios.get(`po-document/order/${order.id}/po-marking`);
                          window.open(data.url, '_blank');
                        } catch { toast.error("Failed to open marking"); }
                      }}
                    >
                      View Marking
                    </Button>
                  ) : order.items?.some(i => i.is_printing) ? (
                    <Tag color="red">⚠ Missing</Tag>
                  ) : (
                    <Text type="secondary">Not uploaded</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Expected Delivery">
                  {order.expected_delivery
                    ? moment(order.expected_delivery).format("DD MMM YYYY")
                    : "-"}
                </Descriptions.Item>
                {order.dispatched_at && (
                  <Descriptions.Item label="Dispatched On">
                    {moment(order.dispatched_at).format("DD MMM YYYY, hh:mm A")}
                  </Descriptions.Item>
                )}
                {order.dispatch_history?.length > 0 ? (
                  <Descriptions.Item label="Dispatch Challans" span={2}>
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      {order.dispatch_history.map((entry, index) => (
                        <div key={`${entry.dispatch_number || entry.receipt_number || "dispatch"}-${index}`}>
                          <Space wrap size={8}>
                            <Tag color="blue">Delivery Challan</Tag>
                            <Text strong>{entry.receipt_number || "-"}</Text>
                            {entry.dispatch_date && (
                              <Text type="secondary">
                                {moment(entry.dispatch_date).format("DD MMM YYYY")}
                              </Text>
                            )}
                            {entry.total_quantity > 0 && (
                              <Text type="secondary">
                                Qty: {Number(entry.total_quantity).toLocaleString("en-IN")}
                              </Text>
                            )}
                          </Space>
                          {entry.notes && (
                            <div>
                              <Text type="secondary">{entry.notes}</Text>
                            </div>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Descriptions.Item>
                ) : order.dispatch_receipt_number && (
                  <Descriptions.Item label="Dispatch Document">
                    <Tag color="blue">Delivery Challan</Tag>{" "}
                    {order.dispatch_receipt_number || "-"}
                  </Descriptions.Item>
                )}
                {order.dispatch_notes && !order.dispatch_history?.length && (
                  <Descriptions.Item label="Dispatch Notes" span={2}>
                    {order.dispatch_notes}
                  </Descriptions.Item>
                )}
                {order.actual_delivery && (
                  <Descriptions.Item label="Delivered On">
                    {moment(order.actual_delivery).format("DD MMM YYYY, hh:mm A")}
                  </Descriptions.Item>
                )}
                {order.delivery_receipt_type && (
                  <Descriptions.Item label="Delivery Document">
                    <Tag color="green">Invoice</Tag>{" "}
                    {order.delivery_receipt_number || "-"}
                  </Descriptions.Item>
                )}
                {order.delivery_notes && (
                  <Descriptions.Item label="Delivery Notes" span={2}>
                    {order.delivery_notes}
                  </Descriptions.Item>
                )}
                {order.receiving_image_url && (
                  <Descriptions.Item label="Receiving Image">
                    <Button
                      type="link"
                      size="small"
                      onClick={() => window.open(order.receiving_image_url, "_blank")}
                    >
                      View Receiving
                    </Button>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created">
                  {moment(order.created_at).format("DD MMM YYYY, hh:mm A")}
                </Descriptions.Item>
                {order.customer?.phone && (
                  <Descriptions.Item label="Customer Phone">
                    {order.customer.phone}
                  </Descriptions.Item>
                )}
                {order.quotation_number && (
                  <Descriptions.Item label="Source Quotation">
                    <Link to={`/quotation/${order.quotation_id}`}>
                      {order.quotation_number}
                    </Link>
                  </Descriptions.Item>
                )}
                {order.cancelled_at && (
                  <Descriptions.Item label="Cancelled At">
                    {moment(order.cancelled_at).format(
                      "DD MMM YYYY, hh:mm A"
                    )}
                  </Descriptions.Item>
                )}
                {order.cancellation_reason && (
                  <Descriptions.Item label="Cancel Reason">
                    <Text type="danger">{order.cancellation_reason}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Order Items */}
            <Card
              bordered={false}
              title="Order Items"
              style={{ marginBottom: 16 }}
              extra={
                <Input.Search
                  allowClear
                  placeholder="Search order items"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  style={{ width: 240, maxWidth: "100%" }}
                />
              }
            >
              <Table
                columns={itemColumns}
                dataSource={filteredItems}
                rowKey="id"
                pagination={false}
                scroll={{ x: 1000 }}
                bordered
                size="small"
              />
            </Card>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={poPreviewUrl ? 12 : 8}>
            {/* PO Document Preview */}
            {poPreviewUrl && (
              <Card
                bordered={false}
                title="📄 PO Document"
                style={{ marginBottom: 16 }}
                extra={
                  <Space size="small">
                    <Button size="small" onClick={() => window.open(poPreviewUrl, '_blank')}>
                      Open in Tab
                    </Button>
                    <Button size="small" danger onClick={() => setPoPreviewUrl(null)}>
                      Close
                    </Button>
                  </Space>
                }
              >
                <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden', background: '#f5f5f5' }}>
                  {poPreviewUrl.split('?')[0].toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={poPreviewUrl + '#navpanes=0&scrollbar=1'}
                      style={{ width: '100%', height: 'calc(100vh - 280px)', border: 'none' }}
                      title="PO Document Preview"
                    />
                  ) : (
                    <img
                      src={poPreviewUrl}
                      alt="PO Document"
                      style={{ width: '100%', maxHeight: 'calc(100vh - 280px)', objectFit: 'contain', display: 'block' }}
                    />
                  )}
                </div>
              </Card>
            )}
            {/* Production Progress */}
            <Card
              bordered={false}
              title="Production Progress"
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <div>
                  <Text>
                    Production: {producedQty.toLocaleString("en-IN")} /{" "}
                    {totalQty.toLocaleString("en-IN")}
                  </Text>
                  <Progress
                    percent={productionPct}
                    status={productionPct === 100 ? "success" : "active"}
                  />
                </div>
                <div>
                  <Text>
                    Delivery: {deliveredQty.toLocaleString("en-IN")} /{" "}
                    {totalQty.toLocaleString("en-IN")}
                  </Text>
                  <Progress
                    percent={deliveryPct}
                    status={deliveryPct === 100 ? "success" : "active"}
                    strokeColor="#52c41a"
                  />
                </div>
              </Space>
            </Card>

            {/* Order Summary */}
            <Card
              bordered={false}
              title="Order Summary"
              style={{ marginBottom: 16 }}
            >
              <div
                style={{
                  background: "#fafafa",
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <Row justify="space-between" style={{ marginBottom: 8 }}>
                  <span>Subtotal:</span>
                  <strong>
                    ₹
                    {Number(order.subtotal || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </strong>
                </Row>
                {(order.discount_percent || 0) > 0 && (
                  <Row justify="space-between" style={{ marginBottom: 8 }}>
                    <span>Discount ({order.discount_percent}%):</span>
                    <span style={{ color: "#f5222d" }}>
                      - ₹
                      {Number(order.discount_amount || 0).toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 }
                      )}
                    </span>
                  </Row>
                )}
                <Row justify="space-between" style={{ marginBottom: 8 }}>
                  <span>Tax ({order.tax_rate || 0}%):</span>
                  <span>
                    ₹
                    {Number(order.tax_amount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </Row>
                <Divider style={{ margin: "8px 0" }} />
                <Row
                  justify="space-between"
                  style={{ fontSize: 18, fontWeight: "bold" }}
                >
                  <span>Total:</span>
                  <span>
                    ₹
                    {Number(order.total_amount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </Row>
              </div>
            </Card>

            {/* Notes / Shipping */}
            {(order.notes ||
              order.shipping_address) && (
              <Card bordered={false} title="Additional Info">
                {order.shipping_address && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Shipping Address:</Text>
                    <p style={{ marginTop: 4 }}>{order.shipping_address}</p>
                  </div>
                )}
                {order.notes && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Notes:</Text>
                    <p style={{ marginTop: 4 }}>{order.notes}</p>
                  </div>
                )}
              </Card>
            )}
          </Col>
        </Row>
      </div>

      <Modal
        title="Record Dispatch"
        open={dispatchModalVisible}
        onCancel={closeDispatchModal}
        onOk={() => dispatchForm.submit()}
        okText="Save Dispatch"
        confirmLoading={dispatchSubmitting}
        destroyOnClose
      >
        <Form
          form={dispatchForm}
          layout="vertical"
          initialValues={{ dispatched_on: moment() }}
          onFinish={handleMarkDispatched}
        >
          {autoDispatchWarning && (
            <Alert
              type="warning"
              showIcon
              message={autoDispatchWarning}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item name="dispatched_on" label="Dispatched On">
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>

          <Form.Item
            name="challan_number"
            label="Challan Number"
            rules={[{ required: true, message: "Challan number is required" }]}
          >
            <Input placeholder="Enter challan number" />
          </Form.Item>

          <Form.Item name="notes" label="Dispatch Notes">
            <Input.TextArea rows={3} placeholder="Enter dispatch notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Record Delivery"
        open={deliveryModalVisible}
        onCancel={closeDeliveryModal}
        onOk={() => deliveryForm.submit()}
        okText="Save Delivery"
        confirmLoading={deliverySubmitting}
        destroyOnClose
      >
        <Form
          form={deliveryForm}
          layout="vertical"
          initialValues={{ delivered_on: moment() }}
          onFinish={handleMarkDelivered}
        >
          {autoDispatchWarning && (
            <Alert
              type="warning"
              showIcon
              message={autoDispatchWarning}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            name="delivered_on"
            label="Delivered On"
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          </Form.Item>

          <Form.Item
            name="invoice_number"
            label="Invoice Number"
            rules={[
              {
                required: true,
                message: "Invoice number is required",
              },
            ]}
          >
            <Input placeholder="Enter invoice number" />
          </Form.Item>

          <Form.Item name="notes" label="Delivery Notes">
            <Input.TextArea rows={3} placeholder="Enter delivery notes" />
          </Form.Item>

          <Form.Item label="Receiving Image">
            <Upload
              accept="image/*"
              beforeUpload={(file) => {
                setReceivingFile(file);
                return false;
              }}
              onRemove={() => {
                setReceivingFile(null);
              }}
              maxCount={1}
              fileList={receivingFile ? [receivingFile] : []}
            >
              <Button icon={<UploadOutlined />}>Upload Receiving</Button>
            </Upload>
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Optional. Upload customer receiving proof image if available.
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Jobs Modal */}
      <Modal
        title={`Create Jobs for: ${selectedItem?.item_name || ""}`}
        open={jobModalVisible}
        onCancel={() => {
          setJobModalVisible(false);
          jobForm.resetFields();
          setSelectedItem(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedItem && (
          <div style={{ marginBottom: 16, background: "#fafafa", padding: 12, borderRadius: 8 }}>
            <Text type="secondary">
              {selectedItem.subcategory} | {selectedItem.ply} Ply |{" "}
              {selectedItem.length}×{selectedItem.width}×{selectedItem.height}{" "}
              {selectedItem.unit_type}
            </Text>
            <br />
            <Text>
              Total Quantity: <strong>{selectedItem.quantity}</strong>
            </Text>
            {selectedItem.jobs && selectedItem.jobs.length > 0 && (
              <>
                <br />
                <Text type="secondary">
                  Already assigned:{" "}
                  {selectedItem.jobs.reduce(
                    (sum, j) => sum + (j.quantity_assigned || 0),
                    0
                  )}
                </Text>
              </>
            )}
          </div>
        )}
        <Form
          form={jobForm}
          layout="vertical"
          onFinish={handleCreateJobs}
        >
          <Form.Item
            name="job_count"
            label="Number of Jobs"
            rules={[{ required: true, message: "Please enter number of jobs" }]}
          >
            <InputNumber min={1} max={100} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="quantity_per_job"
            label="Quantity per Job"
            rules={[{ required: true, message: "Please enter quantity" }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="priority" label="Priority">
            <Select placeholder="Same as order" allowClear>
              <Select.Option value="urgent">Urgent</Select.Option>
              <Select.Option value="high">High</Select.Option>
              <Select.Option value="normal">Normal</Select.Option>
              <Select.Option value="low">Low</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={jobLoading}
                shape="round"
              >
                Create Jobs
              </Button>
              <Button
                onClick={() => {
                  setJobModalVisible(false);
                  jobForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OrderDetail;
